<template>
  <div class="MapFilters" :class="{ 'is-expanded': isExpanded }">
    <h3 class="MapFilters-title" v-if="label">{{ label }}</h3>
    <ul class="MapFilters-options">
      <li
        v-for="option in options"
        :key="getOptionKey(option)"
        class="MapFilters-option"
        :class="{
          'is-active': getOptionKey(option) === getOptionKey(sliderContext),
        }"
        @click="choose(option)"
      >
        {{ option.label }}
        <atlas-icon
          class="MapFilters-selectArrows"
          v-if="!isExpanded"
          name="select-arrows"
          :size="21"
        />
      </li>
    </ul>

    <div class="MapFilters-content" v-if="!isExpanded">
      <div class="MapFilters-desc">
        {{ mapSliderCaptionWithDefault }}
        <strong>{{ valuePercent }}</strong>
      </div>

      <input
        type="range"
        class="MapFilters-slider"
        v-model="value"
        min="0"
        max="1"
        step="0.01"
        :disabled="sliderContext.disable"
      />

      <div class="MapFilters-domain">
        <span class="MapFilters-label">0%</span>
        <span class="MapFilters-label">50%</span>
        <span class="MapFilters-label">100%</span>
      </div>
    </div>

    <div class="MapFilters-footer">
      <atlas-icon
        class="MapFilters-close"
        name="close--outline"
        title="Close"
        :size="21"
        @click.prevent="closeMapFilters"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';

export default defineComponent({
  props: {
    breakdownId: {
      type: String,
      required: true,
    },

    label: {
      type: String,
    },

    options: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    isExpanded: false,
  }),

  computed: {
    ...mapState('breakdown/mapFilters', ['sliderValue']),

    ...mapGetters('breakdown/mapFilters', ['sliderContext']),

    ...mapGetters('breakdown', ['mapSliderCaptionByBreakdownId']),

    mapSliderCaption() {
      return this.mapSliderCaptionByBreakdownId(this.breakdownId);
    },
    mapSliderCaptionWithDefault() {
      return (
        this.mapSliderCaption || 'Show areas where the population is at least:'
      );
    },

    value: {
      get() {
        return this.sliderValue;
      },

      set(value) {
        this.setSliderValue({ value: Number(value) });
      },
    },

    valuePercent() {
      return `${Math.round(this.value * 100)}%`;
    },
  },

  methods: {
    ...mapMutations('ui', ['closeMapFilters']),

    ...mapActions('breakdown/mapFilters', [
      'setSliderContext',
      'setSliderValue',
    ]),

    choose(context) {
      this.isExpanded = !this.isExpanded;

      if (!this.isExpanded) {
        if (context && context.reset) this.value = 0;
        this.setSliderContext({ context });
      }
    },

    reset(context = null) {
      this.value = 0;
      this.setSliderContext({ context });
    },

    getOptionKey: (option) => `${option.context}:${option.columnName}`,
  },

  mounted() {
    if (this.sliderContext) {
      this.setSliderContext({ context: this.sliderContext });
    }
  },

  unmounted() {
    this.reset();
  },
});
</script>

<style lang="scss">
/* stylelint-disable selector-pseudo-element-no-unknown */

.MapFilters {
  &-title {
    padding: 13px $gutter 10px;
    margin: 0;
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
  }

  &-options {
    display: block;
    padding: 0;
    margin: 0;
  }

  &-option {
    position: relative;
    display: none;
    overflow: hidden;
    width: 100%;
    padding: 0 30px 0 $gutter;
    margin: 0;
    background-color: color(rule);
    cursor: pointer;
    font-size: 15px;
    font-weight: normal;
    line-height: 45px;
    text-overflow: ellipsis;
    white-space: nowrap;

    .MapFilters.is-expanded &,
    &.is-active {
      display: block;
    }

    .MapFilters.is-expanded & {
      border-bottom: 1px solid color(rule);
      background-color: transparent;

      &:hover {
        background-color: color(rule);
      }

      &:last-child {
        border-bottom: 0;
      }
    }
  }

  &-selectArrows {
    @include position(absolute, 14px 5px null null);
    color: color(primary);
  }

  &-content {
    padding: 15px;
    font-weight: normal;
  }

  &-desc {
    margin-bottom: 12px;
  }

  &-domain {
    @include clearfix;
    text-align: center;
  }

  &-label {
    display: inline-block;
    margin-top: 5px;
    font-size: 9px;

    &:first-child {
      float: left;
      text-align: left;
    }

    &:last-child {
      float: right;
    }
  }

  &-slider {
    width: 100%;
    margin: 0;
    appearance: none;

    &:focus {
      outline: none;
    }

    &::range-track {
      height: 3px;
      background: color(rule);
      cursor: pointer;
    }

    &::range-thumb {
      display: block;
      width: 0;
      height: 0;
      border-width: 0 6px 12px;
      border-style: solid;
      border-color: transparent transparent color(primary) transparent;
      appearance: none;
      transform: translateY(-4px) rotate(180deg);
    }

    &[disabled]::range-thumb {
      display: none;
    }
  }

  &-footer {
    border-top: 1px solid color(rule);
  }

  &-close {
    margin: 10px $gutter;
    color: color(primary);
    cursor: pointer;

    &:hover {
      color: color(black);
    }
  }
}
</style>
